import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, FileText, Bell, Settings, LogOut, Search,
  CheckCircle, XCircle, Clock, DollarSign, Users, Filter,
  RefreshCw, ChevronRight, Menu, X, Download, Upload,
  MessageCircle, Eye, AlertCircle, Plane, Building2
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Register service worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('SW registered:', registration);
      return registration;
    } catch (error) {
      console.error('SW registration failed:', error);
    }
  }
};

// Request notification permission
const requestNotificationPermission = async () => {
  if ('Notification' in window) {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  return false;
};

const AdminApp = () => {
  const navigate = useNavigate();
  const { token, logout, isAuthenticated, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  
  // Get token from context or localStorage
  const getToken = () => token || localStorage.getItem('admin_token');
  
  // Dashboard stats
  const [stats, setStats] = useState({
    todayNew: 0,
    pending: 0,
    approvedToday: 0,
    rejectedToday: 0,
    totalRevenue: 0,
    b2bPending: 0
  });
  
  // Applications
  const [applications, setApplications] = useState([]);
  const [b2bApplications, setB2bApplications] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'b2c', 'b2b'
  const [showFilters, setShowFilters] = useState(false);
  
  // Selected application for actions
  const [selectedApp, setSelectedApp] = useState(null);
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Check authentication - wait for loading to complete
  useEffect(() => {
    // Check localStorage directly as fallback
    const storedToken = localStorage.getItem('admin_token');
    if (!authLoading && !isAuthenticated && !storedToken) {
      // Store that we want to come back to the mobile app
      localStorage.setItem('admin_redirect', '/admin/app');
      navigate('/admin?redirect=/admin/app');
    }
    // If no token at all, stop loading
    if (!storedToken && !isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Initialize PWA features
  useEffect(() => {
    registerServiceWorker();
    requestNotificationPermission().then(setNotificationsEnabled);
  }, []);

  // Fetch data with timeout for mobile networks
  const fetchData = useCallback(async () => {
    const currentToken = getToken();
    if (!currentToken) {
      setLoading(false);
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${currentToken}` };
      const timeout = 15000; // 15 second timeout
      
      // Fetch B2C applications
      const b2cRes = await axios.get(`${API}/admin/applications`, { headers, timeout });
      const b2cApps = b2cRes.data || [];
      
      // Fetch B2B applications
      const b2bRes = await axios.get(`${API}/admin/b2b/applications`, { headers, timeout });
      const b2bApps = b2bRes.data || [];
      
      setApplications(b2cApps);
      setB2bApplications(b2bApps);
      
      // Calculate stats
      const today = new Date().toISOString().split('T')[0];
      const allApps = [...b2cApps, ...b2bApps.map(a => ({ ...a, isB2B: true }))];
      
      const todayNew = allApps.filter(a => a.created_at?.startsWith(today)).length;
      const pending = allApps.filter(a => a.status === 'pending' || a.status === 'processing').length;
      const approvedToday = allApps.filter(a => a.status === 'approved' && a.updated_at?.startsWith(today)).length;
      const rejectedToday = allApps.filter(a => a.status === 'rejected' && a.updated_at?.startsWith(today)).length;
      const b2bPending = b2bApps.filter(a => a.status === 'pending').length;
      
      // Calculate revenue from paid B2C apps
      const totalRevenue = b2cApps
        .filter(a => a.payment_status === 'paid')
        .reduce((sum, a) => sum + (a.total_visa_fee || 0), 0);
      
      setStats({
        todayNew,
        pending,
        approvedToday,
        rejectedToday,
        totalRevenue,
        b2bPending
      });
      
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        // Token expired or invalid - clear and redirect
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_email');
        localStorage.removeItem('session_id');
        localStorage.setItem('admin_redirect', '/admin/app');
        logout();
        navigate('/admin?redirect=/admin/app');
      } else if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Connection timeout. Please check your internet.');
      } else if (!navigator.onLine) {
        toast.error('You appear to be offline');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [logout, navigate]);

  useEffect(() => {
    const currentToken = getToken();
    if (currentToken) {
      fetchData();
    } else {
      // No token - stop loading and redirect will happen
      setLoading(false);
    }
    // Auto-refresh every 30 seconds only if we have a token
    const interval = setInterval(() => {
      if (getToken()) {
        fetchData();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Pull to refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    toast.success('Data refreshed');
  };

  // Filter applications
  const getFilteredApplications = () => {
    let allApps = [];
    
    if (typeFilter === 'all' || typeFilter === 'b2c') {
      allApps = [...allApps, ...applications.map(a => ({ ...a, type: 'B2C' }))];
    }
    if (typeFilter === 'all' || typeFilter === 'b2b') {
      allApps = [...allApps, ...b2bApplications.map(a => ({ ...a, type: 'B2B' }))];
    }
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      allApps = allApps.filter(a => 
        a.first_name?.toLowerCase().includes(query) ||
        a.last_name?.toLowerCase().includes(query) ||
        a.passport_number?.toLowerCase().includes(query) ||
        a.id?.toLowerCase().includes(query) ||
        a.applicant_name?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      allApps = allApps.filter(a => a.status === statusFilter);
    }
    
    // Sort by date (newest first)
    allApps.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    return allApps;
  };

  // Status update
  const updateStatus = async (appId, newStatus, isB2B = false) => {
    setActionLoading(true);
    try {
      const endpoint = isB2B 
        ? `${API}/admin/b2b/applications/${appId}/status`
        : `${API}/admin/applications/${appId}/status`;
      
      await axios.put(endpoint, { status: newStatus }, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      
      toast.success(`Status updated to ${newStatus}`);
      setShowActionSheet(false);
      setSelectedApp(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  // Open WhatsApp with pre-filled message
  const openWhatsApp = (app) => {
    const name = app.applicant_name || `${app.first_name} ${app.last_name}`;
    const phone = app.user_phone || app.phone || '';
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    
    let message = '';
    if (app.status === 'approved') {
      message = `Hello ${name},\n\nGreat news! Your visa application (${app.id}) has been approved.\n\nYou can download your visa from our website.\n\nThank you for choosing A-One Tourism!`;
    } else if (app.status === 'rejected') {
      message = `Hello ${name},\n\nRegarding your visa application (${app.id}):\n\nUnfortunately, your application could not be approved at this time.\n\nPlease contact us for more information.\n\nA-One Tourism`;
    } else {
      message = `Hello ${name},\n\nRegarding your visa application (${app.id}):\n\n[Your message here]\n\nA-One Tourism`;
    }
    
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      sent_back: 'bg-orange-100 text-orange-800',
      draft: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Render Dashboard
  const renderDashboard = () => (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-semibold text-gray-800">Today&apos;s Overview</h2>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded-full bg-gray-100 active:bg-gray-200"
        >
          <RefreshCw className={`w-5 h-5 text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <motion.div 
          whileTap={{ scale: 0.98 }}
          className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg"
        >
          <div className="flex items-center justify-between">
            <FileText className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.todayNew}</span>
          </div>
          <p className="text-sm mt-2 opacity-90">New Today</p>
        </motion.div>
        
        <motion.div 
          whileTap={{ scale: 0.98 }}
          className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-4 text-white shadow-lg"
        >
          <div className="flex items-center justify-between">
            <Clock className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.pending}</span>
          </div>
          <p className="text-sm mt-2 opacity-90">Pending</p>
        </motion.div>
        
        <motion.div 
          whileTap={{ scale: 0.98 }}
          className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-4 text-white shadow-lg"
        >
          <div className="flex items-center justify-between">
            <CheckCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.approvedToday}</span>
          </div>
          <p className="text-sm mt-2 opacity-90">Approved</p>
        </motion.div>
        
        <motion.div 
          whileTap={{ scale: 0.98 }}
          className="bg-gradient-to-br from-red-500 to-rose-500 rounded-xl p-4 text-white shadow-lg"
        >
          <div className="flex items-center justify-between">
            <XCircle className="w-8 h-8 opacity-80" />
            <span className="text-3xl font-bold">{stats.rejectedToday}</span>
          </div>
          <p className="text-sm mt-2 opacity-90">Rejected</p>
        </motion.div>
      </div>
      
      {/* Revenue Card */}
      <motion.div 
        whileTap={{ scale: 0.99 }}
        className="bg-gradient-to-r from-[#1A4D7F] to-[#009FB7] rounded-xl p-5 text-white shadow-lg"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">Total Revenue (B2C)</p>
            <p className="text-3xl font-bold mt-1">{stats.totalRevenue.toLocaleString()} AED</p>
          </div>
          <DollarSign className="w-12 h-12 opacity-50" />
        </div>
      </motion.div>
      
      {/* B2B Card */}
      <motion.div 
        whileTap={{ scale: 0.99 }}
        onClick={() => { setTypeFilter('b2b'); setActiveTab('applications'); }}
        className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <Building2 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="font-medium text-gray-800">B2B Applications</p>
            <p className="text-sm text-gray-500">{stats.b2bPending} pending review</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-gray-400" />
      </motion.div>
      
      {/* Quick Actions */}
      <div className="mt-6">
        <h3 className="text-sm font-medium text-gray-500 mb-3">QUICK ACTIONS</h3>
        <div className="grid grid-cols-3 gap-3">
          <button 
            onClick={() => { setStatusFilter('pending'); setActiveTab('applications'); }}
            className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100"
          >
            <Clock className="w-6 h-6 text-amber-500 mb-2" />
            <span className="text-xs text-gray-600">Pending</span>
          </button>
          <button 
            onClick={() => { setStatusFilter('processing'); setActiveTab('applications'); }}
            className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100"
          >
            <Plane className="w-6 h-6 text-blue-500 mb-2" />
            <span className="text-xs text-gray-600">Processing</span>
          </button>
          <button 
            onClick={() => setActiveTab('applications')}
            className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100"
          >
            <Search className="w-6 h-6 text-gray-500 mb-2" />
            <span className="text-xs text-gray-600">Search</span>
          </button>
        </div>
      </div>
    </div>
  );

  // Render Applications List
  const renderApplications = () => {
    const filteredApps = getFilteredApplications();
    
    return (
      <div className="flex flex-col h-full">
        {/* Search Bar */}
        <div className="p-4 bg-white border-b sticky top-0 z-10">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search passport, ID, name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`p-3 rounded-xl ${showFilters ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>
          
          {/* Filters */}
          <AnimatePresence>
            {showFilters && (
              <motion.div 
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mt-3 overflow-hidden"
              >
                <div className="flex gap-2 mb-2">
                  <select 
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="flex-1 p-2 bg-gray-100 rounded-lg text-sm"
                  >
                    <option value="all">All Types</option>
                    <option value="b2c">B2C Only</option>
                    <option value="b2b">B2B Only</option>
                  </select>
                  <select 
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="flex-1 p-2 bg-gray-100 rounded-lg text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="sent_back">Sent Back</option>
                  </select>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Applications List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filteredApps.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No applications found</p>
            </div>
          ) : (
            filteredApps.map((app) => (
              <motion.div
                key={app.id}
                whileTap={{ scale: 0.98 }}
                onClick={() => { setSelectedApp(app); setShowActionSheet(true); }}
                className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${app.type === 'B2B' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {app.type}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </div>
                    <h3 className="font-medium text-gray-800 mt-2">
                      {app.applicant_name || `${app.first_name} ${app.last_name}`}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      {app.passport_number} • {app.nationality}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      ID: {app.id?.slice(0, 8)}...
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500">
                    {new Date(app.created_at).toLocaleDateString()}
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    {app.total_visa_fee || app.total_amount || 0} AED
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </div>
    );
  };

  // Action Sheet
  const renderActionSheet = () => {
    if (!selectedApp) return null;
    const isB2B = selectedApp.type === 'B2B';
    const name = selectedApp.applicant_name || `${selectedApp.first_name} ${selectedApp.last_name}`;
    
    return (
      <AnimatePresence>
        {showActionSheet && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowActionSheet(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-h-[80vh] overflow-y-auto"
            >
              <div className="p-4">
                <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />
                
                {/* App Info */}
                <div className="text-center mb-4">
                  <span className={`text-xs px-2 py-1 rounded-full ${isB2B ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                    {selectedApp.type}
                  </span>
                  <h3 className="text-xl font-semibold mt-2">{name}</h3>
                  <p className="text-gray-500">{selectedApp.passport_number}</p>
                  <p className="text-sm text-gray-400 mt-1">
                    {selectedApp.nationality} • {selectedApp.visa_duration}
                  </p>
                </div>
                
                {/* Current Status */}
                <div className="flex items-center justify-center gap-2 mb-6">
                  <span className="text-sm text-gray-500">Current:</span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedApp.status)}`}>
                    {selectedApp.status}
                  </span>
                </div>
                
                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => updateStatus(selectedApp.id, 'approved', isB2B)}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 p-4 bg-green-500 text-white rounded-xl font-medium active:bg-green-600 disabled:opacity-50"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Approve
                  </button>
                  <button
                    onClick={() => updateStatus(selectedApp.id, 'rejected', isB2B)}
                    disabled={actionLoading}
                    className="flex items-center justify-center gap-2 p-4 bg-red-500 text-white rounded-xl font-medium active:bg-red-600 disabled:opacity-50"
                  >
                    <XCircle className="w-5 h-5" />
                    Reject
                  </button>
                </div>
                
                {/* More Actions */}
                <div className="space-y-2">
                  <button
                    onClick={() => updateStatus(selectedApp.id, 'processing', isB2B)}
                    disabled={actionLoading}
                    className="w-full flex items-center gap-3 p-4 bg-gray-100 rounded-xl active:bg-gray-200"
                  >
                    <Clock className="w-5 h-5 text-blue-500" />
                    <span>Mark as Processing</span>
                  </button>
                  
                  <button
                    onClick={() => updateStatus(selectedApp.id, 'sent_back', isB2B)}
                    disabled={actionLoading}
                    className="w-full flex items-center gap-3 p-4 bg-gray-100 rounded-xl active:bg-gray-200"
                  >
                    <AlertCircle className="w-5 h-5 text-orange-500" />
                    <span>Request Documents</span>
                  </button>
                  
                  <button
                    onClick={() => openWhatsApp(selectedApp)}
                    className="w-full flex items-center gap-3 p-4 bg-gray-100 rounded-xl active:bg-gray-200"
                  >
                    <MessageCircle className="w-5 h-5 text-green-500" />
                    <span>Send WhatsApp</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowActionSheet(false);
                      navigate(`/admin/dashboard?app=${selectedApp.id}&type=${selectedApp.type}`);
                    }}
                    className="w-full flex items-center gap-3 p-4 bg-gray-100 rounded-xl active:bg-gray-200"
                  >
                    <Eye className="w-5 h-5 text-gray-500" />
                    <span>View Full Details</span>
                  </button>
                  
                  {selectedApp.status === 'approved' && (
                    <button
                      onClick={() => {
                        setShowActionSheet(false);
                        navigate(`/admin/dashboard?app=${selectedApp.id}&upload=visa`);
                      }}
                      className="w-full flex items-center gap-3 p-4 bg-[#1A4D7F] text-white rounded-xl active:bg-[#153d66]"
                    >
                      <Upload className="w-5 h-5" />
                      <span>Upload Visa PDF</span>
                    </button>
                  )}
                </div>
                
                <button
                  onClick={() => setShowActionSheet(false)}
                  className="w-full mt-4 p-4 text-gray-500 font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    );
  };

  // Render Settings
  const renderSettings = () => (
    <div className="p-4 space-y-4">
      <h2 className="text-lg font-semibold text-gray-800 mb-4">Settings</h2>
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <Bell className="w-5 h-5 text-gray-500" />
            <span>Push Notifications</span>
          </div>
          <span className={`text-sm ${notificationsEnabled ? 'text-green-600' : 'text-gray-400'}`}>
            {notificationsEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        
        <button 
          onClick={() => navigate('/admin/dashboard')}
          className="w-full p-4 flex items-center gap-3 border-b border-gray-100 active:bg-gray-50"
        >
          <LayoutDashboard className="w-5 h-5 text-gray-500" />
          <span>Full Admin Dashboard</span>
          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
        </button>
        
        <button 
          onClick={async () => {
            await logout();
            navigate('/admin');
          }}
          className="w-full p-4 flex items-center gap-3 text-red-600 active:bg-gray-50"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
      
      {/* Install App Prompt */}
      <div className="bg-gradient-to-r from-[#1A4D7F] to-[#009FB7] rounded-xl p-4 text-white">
        <h3 className="font-medium mb-2">Install App</h3>
        <p className="text-sm opacity-90 mb-3">
          Add this app to your home screen for quick access and push notifications.
        </p>
        <p className="text-xs opacity-75">
          Tap the share button and select &quot;Add to Home Screen&quot;
        </p>
      </div>
    </div>
  );

  // Loading timeout - don't spin forever
  useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        if (loading) {
          setLoading(false);
          const hasToken = getToken();
          if (!hasToken) {
            localStorage.setItem('admin_redirect', '/admin/app');
            navigate('/admin?redirect=/admin/app');
          } else {
            toast.error('Loading took too long. Pull down to refresh.');
          }
        }
      }, 20000); // 20 second max loading
      return () => clearTimeout(timeout);
    }
  }, [loading, navigate]);

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        <p className="text-gray-500 text-sm mt-4">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="admin-app">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-[#1A4D7F] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <img src="/logo192.png" alt="A-One" className="w-8 h-8 rounded-lg" />
          <h1 className="font-semibold">A-One Admin</h1>
        </div>
        <button 
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 rounded-full hover:bg-white/10"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 overflow-hidden">
        {activeTab === 'dashboard' && renderDashboard()}
        {activeTab === 'applications' && renderApplications()}
        {activeTab === 'settings' && renderSettings()}
      </main>
      
      {/* Bottom Navigation */}
      <nav className="bg-white border-t border-gray-200 px-6 py-2 flex justify-around items-center sticky bottom-0 z-20">
        <button 
          onClick={() => setActiveTab('dashboard')}
          className={`flex flex-col items-center py-2 px-4 ${activeTab === 'dashboard' ? 'text-[#1A4D7F]' : 'text-gray-400'}`}
        >
          <LayoutDashboard className="w-6 h-6" />
          <span className="text-xs mt-1">Dashboard</span>
        </button>
        <button 
          onClick={() => setActiveTab('applications')}
          className={`flex flex-col items-center py-2 px-4 ${activeTab === 'applications' ? 'text-[#1A4D7F]' : 'text-gray-400'}`}
        >
          <FileText className="w-6 h-6" />
          <span className="text-xs mt-1">Applications</span>
        </button>
        <button 
          onClick={() => setActiveTab('settings')}
          className={`flex flex-col items-center py-2 px-4 ${activeTab === 'settings' ? 'text-[#1A4D7F]' : 'text-gray-400'}`}
        >
          <Settings className="w-6 h-6" />
          <span className="text-xs mt-1">Settings</span>
        </button>
      </nav>
      
      {/* Action Sheet */}
      {renderActionSheet()}
    </div>
  );
};

export default AdminApp;
